<template>
  <t-card max-width="600" class="d-flex flex-column justify-start">
    <v-card-title>
      <v-row class="py-4 px-4">
        <v-col>
          <span v-if="dialog.config.hasTitle">ACESSAR CONTA</span>
        </v-col>
        <v-spacer />
        <v-btn v-if="dialog.config.hasCloseButton" icon elevation="0">
          <v-icon size="small" @click="close">
            mdi-close
          </v-icon>
        </v-btn>
      </v-row>
    </v-card-title>
    <v-card-text>
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
        @submit.prevent="login"
      >
        <v-row justify="center" class="my-2">
          <v-col
            cols="8"
            sm="5"
            md="4"
            lg="6"
          >
            <t-logo width="100%" />
          </v-col>
        </v-row>
        <p class="text-subtitle-1 mb-2">
          Informe os dados abaixo para ter acesso a sua conta:
        </p>
        <t-text-input
          v-model="email"
          label="E-mail"
          type="email"
          variant="outlined"
          required
        />
        <t-text-input
          v-model="password"
          label="Senha"
          type="password"
          variant="outlined"
          required
        />
        <v-btn
          color="success"
          block
          type="submit"
          class="py-5"
          :loading="loading"
          :disabled="!valid"
        >
          Acessar
        </v-btn>
        <v-btn
          v-for="integration in integrations"
          :key="integration.id"
          :color="integration.primary_color"
          block
          class="py-5 mt-2"
          :loading="loading"
          :style="{ color: integration.secondary_color }"
          @click="redirect(integration)"
        >
          <v-img
            :src="integration.logo"
            max-width="36"
            max-height="36"
            class="mr-3 py-2"
            cover
          />
          Acessar com {{ integration.name }}
        </v-btn>
      </v-form>
      <a class="d-flex justify-center my-3 text-primary cursor-pointer" @click="forgotPassword"> Esqueci minha senha! </a>
      <v-btn
        color="primary"
        block
        class="py-5"
        @click="register"
      >
        Não tem conta? <span class="font-weight-bold ml-1"> Cadastre-se</span>
      </v-btn>
    </v-card-text>
    <v-spacer />
  </t-card>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import type { ComponentPublicInstance } from "vue";
import { useRouter } from "vue-router";
import TCard from "@/components/core/TCard.vue";
import TTextInput from "@/components/core/TTextInput.vue";
import TLogo from "@/components/core/TLogo.vue";

import getUserData from "@/api/requests/getUserData";
import getAccessToken from "@/api/requests/getAccessToken";
import axios, { AxiosError } from "axios";
import ErrorResponse from "@/types/ErrorResponse";
import { TrackableEvent } from "@/analytics/TrackableEvents";
import Integration from "@/types/Integration";
import { useDialog } from "@/stores/dialog";
import { useSnackbar } from "@/stores/snackbar";
import { useUserStore } from "@/stores/user";
import { useAnalytics } from "@/plugins/analytics";
import { useEventsStore } from "@/stores/events";
import { useIntegrationAuth } from "@/composables/useIntegrationAuth";

// Composables
const router = useRouter();
const userStore = useUserStore();
const dialog = useDialog();
const snackbar = useSnackbar();
const analytics = useAnalytics();

// Refs
const form = ref<ComponentPublicInstance & { validate: () => boolean }>();
const valid = ref(false);
const email = ref("");
const password = ref("");
const loading = ref(false);

// Computed
const integrations = computed<Integration[]>(() => useEventsStore().selected?.integrations ?? []);

// Methods
const login = async () => {
  if (!form.value?.validate()) return;

  loading.value = true;

  try {
    const { data } = await getAccessToken(email.value, password.value);

    userStore.setToken(data);

    const user = (await getUserData()).data;

    userStore.setUser(user);

    analytics.collect(
      TrackableEvent.Login,
      { params: { method: "password" } },
    );

    snackbar.show("Login feito com sucesso!", "success");

    loading.value = false;

    const redirectTo = dialog.config.redirect;

    close();

    if (redirectTo) {
      await router.push(redirectTo);
    }
  } catch (e) {
    if (axios.isAxiosError(e)) {
      const error = e as AxiosError<ErrorResponse>;

      if (error.response?.status == 400) {
        snackbar.show("E-mail ou senha incorretos!", "error");
      } else {
        snackbar.show(`Não foi possível realizar seu login (${error.response?.data.message}), entre em contato com o suporte.`, "error");
      }
    } else {
      snackbar.show("Não foi possível realizar seu login, entre em contato com o suporte.", "error");
    }
  } finally {
    loading.value = false;
  }
};

const register = () => {
  dialog.show("/register");
};

const forgotPassword = () => {
  dialog.show("/recover");
};

const close = () => {
  dialog.hide();
};

const { redirectToIntegration } = useIntegrationAuth();

const redirect = (integration: Integration) => {
  const event = computed(() => useEventsStore().selected);

  if (!event.value) {
    snackbar.show("Evento não encontrado, tente novamente mais tarde.", "error");
    return;
  }

  redirectToIntegration(integration, event.value.cliente_id);
};
</script>

<style lang="scss">
.light-grey {
  background-color: #ccc !important;
}

.clickable {
  cursor: pointer;
}

.uppercase {
  text-transform: uppercase;
}
</style>
